import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { reportsApi, type JobCostReport as Report, type JobCostRow, type JobCostGroup } from '@/lib/apiClient';
import { StateView } from '@/components';
import { fmtDateTime, todayInputValue } from '@/lib/dates';

/**
 * Job Cost Management Report (issue #347).
 *
 * Rebuilds the report E&W lost when they left S&S — the one executives, PMs and
 * estimators actually managed cost by. The brief was that the Intacct
 * equivalent is "confusing or inaccurate", so the whole design goal here is
 * PLAINNESS: one row per cost code, estimated vs actual vs balance, and a
 * balance you can check with a calculator.
 *
 * WHAT IS DIFFERENT FROM THE TWO LEGACY WORKBOOKS
 *
 *  • FIVE cost-group column pairs, not three (#347). The old sheet had
 *    Labor / Material / Subs; our Intacct cost types resolve to five groups, and
 *    Equipment and Other (per diem, travel, fuel) carry real money. Folding them
 *    in would misstate them; dropping them would leave the columns disagreeing
 *    with the job's actual cost.
 *
 *  • Cost codes are CSI-formatted (`03-31-05`), not the legacy 5-digit codes, and
 *    the change block keys off `CO###` codes rather than the retired `8xxxxx`
 *    prefix. The formatting difference was flagged in the issue.
 *
 *  • The legacy change block's Balance columns were BROKEN — they echoed the
 *    section above, so `800214` showed a $-85,251.10 labor balance against a zero
 *    estimate and zero actual. Not reproduced; balance is always est − actual.
 *
 * ⚠️ THIS GROUPS DIFFERENTLY FROM THE FINANCIALS TAB, BY DESIGN. That tab gives
 * each cost code ONE dominant group (matching actuals to the estimate's cost type
 * manufactured phantom overruns in its WIP math). This report splits each code
 * ACROSS groups, because side-by-side Labor / Material / Subs columns are the
 * point of it. The JOB TOTALS still agree — both sum the same two feeds.
 */

// Cents are SHOWN, not rounded away: the feeds carry cent precision, and
// rounding each cell to whole dollars independently made the displayed
// arithmetic false — estimated 100.49 / actual 100.50 / balance −0.01 rendered
// as $100, $101, −$0, visibly breaking the Balance = Estimated − Actual rule
// the report states (review, #352). The legacy Excel sheet showed cents too.
// Rounding to cents FIRST keeps a sub-cent negative from rendering "-$0.00".
const money = (v: number) => {
  const c = Math.round(v * 100) / 100;
  return (c < 0 ? '-' : '') + '$'
    + Math.abs(c).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** A balance cell reads as over / under at a glance — the single thing every
 *  reader of this report is scanning for. Zero stays neutral. Classified at the
 *  SAME cent rounding money() displays, so a rendered −$0.49 colors as the
 *  overrun it says it is and only a true $0.00 stays neutral (review, #352 —
 *  the old whole-dollar Math.round left cent-sized balances uncolored). */
const balanceClass = (v: number) => {
  const c = Math.round(v * 100) / 100;
  return c < 0 ? 'jcr-over' : c > 0 ? 'jcr-under' : '';
};

/**
 * Whether a band draws the line down its left edge (issue #602): every band with
 * another band to its left, i.e. all but the first. The first band's left edge is
 * the Description column's right border, so a line of its own would double it.
 *
 * ONE rule, used for the group labels, the sub-header and every body row, with
 * the Total band indexed as the one after the last group. These were three
 * separate expressions, and the body's Total was hard-coded where the header's
 * was computed — they only agreed because the API happens to send groups.
 */
const isBandStart = (bandIndex: number) => bandIndex > 0;

const css = `
  .jcr-hd { display:flex; align-items:flex-start; justify-content:space-between; gap:var(--ew-s-4); flex-wrap:wrap; margin-bottom:var(--ew-s-4); }
  .jcr-hd h2 { font:var(--ew-fw-semibold) var(--ew-fs-h3)/var(--ew-lh-snug) var(--ew-font); color:var(--ew-dark); margin:0; }
  .jcr-hd p { margin:4px 0 0; font:var(--ew-fw-regular) var(--ew-fs-body-sm)/1.45 var(--ew-font); color:var(--ew-n-500); }
  .jcr-facts { display:grid; grid-template-columns:repeat(auto-fit,minmax(190px,1fr)); gap:var(--ew-s-3) var(--ew-s-5);
    background:#fff; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md); padding:var(--ew-s-4) var(--ew-s-5); margin-bottom:var(--ew-s-4); }
  .jcr-fact .l { font:var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing:.06em; text-transform:uppercase; color:var(--ew-n-500); }
  .jcr-fact .v { font:var(--ew-fw-medium) 13.5px/1.3 var(--ew-font); color:var(--ew-dark); margin-top:5px; }
  .jcr-note { border-radius:var(--ew-r-md); padding:9px 12px; font:var(--ew-fw-regular) 12.5px/1.45 var(--ew-font); margin-bottom:var(--ew-s-3); }
  .jcr-note.warn { background:#FBEEE1; color:#8A4E12; }
  .jcr-note.info { background:var(--ew-n-50); color:var(--ew-n-600); }

  /* The table is genuinely wide (17 columns), so it scrolls INSIDE its own box
     rather than pushing the workspace sideways, and the first two columns stay
     pinned so a row never loses its cost code. */
  .jcr-wrap { background:#fff; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md); overflow:auto; max-height:70vh; }
  /* The table's tunable measures, declared ONCE here as their defaults and read
     everywhere below as a bare var(). A more specific rule overrides them in one
     place (see the compact rule) instead of every use carrying its own copy of
     the number. */
  .jcr-table { border-collapse:separate; border-spacing:0; width:max-content; min-width:100%;
    --jcr-px:10px; --jcr-p1:132px; --jcr-p2:240px; --jcr-fs:12.5px; --jcr-h1:28px;
    font:var(--ew-fw-regular) var(--jcr-fs)/1.35 var(--ew-font); font-variant-numeric:tabular-nums; }
  /* COMPACT — what the Full screen overlay asks for (issue #602). Even full screen,
     on a 1920px monitor, the table as first built still scrolled sideways, so this
     trades density for width: tighter cell padding, a half-point smaller body font
     (the header labels keep their own 11px) and narrower pinned columns. It lives
     here, next to the variables it sets, and is switched on by the compact prop.
     The numbers were tuned against a 1920px-wide window on Windows. Usable width
     also depends on scrollbar width, which differs by platform, so treat them as a
     starting point and re-measure rather than trusting them as constants.
     The description column ellipsizes sooner and keeps its full-name tooltip. The
     cost code column keeps a little room for the longest seeded code at this size
     (13 characters); the column allows 30, so anything longer ellipsizes and
     carries a tooltip rather than sliding under the description.
     ⚠️ THIS IS NOT "EVERY JOB FITS", and that is a decision, not an oversight
     (product owner, 2026-10-06): the columns stay as they are. A job wider than
     the worst case measured for issue #602, and the optional commitment columns
     (30 numeric), will still scroll; Export to Excel is the answer for those.
     Do not drop or merge columns to chase the last few pixels. */
  .jcr-compact .jcr-table { --jcr-px:4px; --jcr-p1:108px; --jcr-p2:150px; --jcr-fs:12px; }
  /* box-sizing is stated HERE, not inherited from the app's global reset: the
     pinned widths and the header-row height below are only exact if they include
     padding and border. Under content-box the 28px header row would render 41px. */
  .jcr-table th, .jcr-table td { box-sizing:border-box; padding:6px var(--jcr-px); white-space:nowrap; border-bottom:1px solid #F2F2F1; }
  .jcr-table thead th { position:sticky; background:var(--ew-n-50); color:var(--ew-n-600); text-align:right;
    font:var(--ew-fw-semibold) 11px/1.2 var(--ew-font); letter-spacing:.04em; text-transform:uppercase; z-index:2; }
  /* The second header row pins at the FIRST row's height. That used to be a
     hard-coded 28px against a first row that actually renders 26.19px, which left
     a ~1.8px gap for body rows to scroll through — visible as clipped slivers of
     text above the Estimated / Actual / Balance labels. The row is now GIVEN that
     height and the second row pins at the same variable, so the two agree by
     construction — as long as the label fits. A cell's height is a minimum, and
     this row's text needs 26.2px of the 28px (11px type at 1.2 line height, plus
     padding and border), so only a forced minimum font size could push it past.
     (Set on the group cells only: the two rowspan cells span both rows anyway.) */
  .jcr-table thead tr:first-child th { top:0; }
  .jcr-table thead tr:first-child th.grp { height:var(--jcr-h1); }
  .jcr-table thead tr:last-child th { top:var(--jcr-h1); border-bottom:1px solid var(--ew-n-200); }
  .jcr-table thead th.grp { text-align:center; }
  /* Band lines (issue #602): the first column of each band after the first — the
     first band's left edge is already the Description column's right border (see
     isBandStart). They run through the group labels, the sub-header and every
     body row from this ONE class: the group labels used to carry their own
     border-left, which both marked the boundary at the very top only and drew a
     second 2px line beside the Description column. With the overlay's tight cell
     padding, figures from adjacent bands sit ~8px apart and need the line all the
     way down. One line per band, NOT per column: the Estimated / Actual / Balance
     trio already reads as a unit. */
  .jcr-table .jcr-bs { border-left:1px solid var(--ew-n-200); }
  .jcr-table td.num { text-align:right; }
  .jcr-table td.first, .jcr-table th.first { text-align:left; }
  /* Pinned identity columns, so a row never loses its cost code while the reader
     scrolls right through 17 columns.
     BOTH WIDTHS ARE FIXED, and .p2's left offset MUST equal .p1's width. A sticky
     offset guessed from the expected text breaks on the real data: prod carries
     suffixed codes such as 03-11-13-464, wider than a plain 03-31-05, which would
     have slid the description column over the top of them. Fixed widths make the
     offset exact instead of approximately right.
     The widths are CUSTOM PROPERTIES (declared once on .jcr-table) so the compact
     layout can tighten them (issue #602). That also turns the rule above from a
     comment into a fact: .p2's left offset and .p1's width read the SAME variable,
     so overriding one cannot leave the other behind.
     The z-index ordering matters too: header cells must sit above pinned body
     cells, or a scrolled row slides over the header.
     (No backticks in this block — the whole stylesheet is a template literal.) */
  .jcr-table th.p1, .jcr-table td.p1 { position:sticky; left:0; background:#fff;
    width:var(--jcr-p1); min-width:var(--jcr-p1); max-width:var(--jcr-p1);
    overflow:hidden; text-overflow:ellipsis; }
  .jcr-table th.p2, .jcr-table td.p2 { position:sticky; left:var(--jcr-p1); background:#fff;
    border-right:1px solid var(--ew-n-200); width:var(--jcr-p2); min-width:var(--jcr-p2); max-width:var(--jcr-p2);
    overflow:hidden; text-overflow:ellipsis; }
  .jcr-table thead th.p1, .jcr-table thead th.p2 { background:var(--ew-n-50); z-index:3; }
  .jcr-table tbody td.p1, .jcr-table tbody td.p2 { z-index:1; }
  .jcr-table tbody tr:hover td { background:#FAFAF9; }
  .jcr-table tbody tr:hover td.p1, .jcr-table tbody tr:hover td.p2 { background:#FAFAF9; }
  .jcr-sec td { background:var(--ew-n-50); font:var(--ew-fw-semibold) 11px/1.2 var(--ew-font);
    letter-spacing:.06em; text-transform:uppercase; color:var(--ew-green-700); padding-top:10px; padding-bottom:10px; }
  .jcr-tot td { font-weight:600; background:#F7F7F6; border-top:1px solid var(--ew-n-300); border-bottom:1px solid var(--ew-n-300); }
  .jcr-tot.grand td { background:var(--ew-dark); color:#fff; border-color:var(--ew-dark); }
  .jcr-tot.grand td.p1, .jcr-tot.grand td.p2 { background:var(--ew-dark); color:#fff; }
  .jcr-tot td.p1, .jcr-tot td.p2 { background:#F7F7F6; }
  .jcr-over { color:var(--ew-danger); }
  .jcr-under { color:var(--ew-green-700); }
  /* Committed is an obligation, not a verdict — deliberately unclassified. */
  .jcr-com { color:var(--ew-n-600); }
  .jcr-tot.grand .jcr-over { color:#FF9F91; }
  .jcr-tot.grand .jcr-under { color:#8FE3B0; }
  .jcr-btn { font:var(--ew-fw-semibold) 12px/1 var(--ew-font); border-radius:var(--ew-r-md); padding:9px 15px; cursor:pointer;
    border:1px solid var(--ew-green); background:var(--ew-green); color:#fff; }
  .jcr-btn:hover { background:var(--ew-green-700); border-color:var(--ew-green-700); }
  .jcr-toggle { display:flex; align-items:center; gap:7px; font:var(--ew-fw-medium) 12.5px/1 var(--ew-font); color:var(--ew-n-600); cursor:pointer; }
  .jcr-empty { padding:var(--ew-s-5); text-align:center; color:var(--ew-n-400); font-size:13px; }
`;

/**
 * `compact` trades density for width: the Full screen overlay passes it (issue
 * #602). It only adds a class, so flipping it never remounts the report — the
 * query cache, scroll position and toggles all survive, which is the whole point
 * of the overlay sharing one subtree with the in-page view.
 */
export default function JobCostReport({ projectId, compact = false }: { projectId: number; compact?: boolean }) {
  const q = useQuery({
    queryKey: ['reports', projectId, 'job-cost'],
    queryFn: () => reportsApi.jobCost(projectId),
  });

  /**
   * Codes with no money on either side are hidden by default.
   *
   * The legacy sheet listed every code on the estimate, and 60 of its ~97 rows
   * were all zeros — the noise that makes a report tiring to read. They stay
   * available behind the toggle because an estimator does sometimes want to see
   * that a code exists and carries nothing.
   */
  const [showZeroRows, setShowZeroRows] = useState(false);

  /**
   * Open commitment + exposure are OFF by default (product owner, 2026-08-21).
   *
   * Five cost groups plus Total already put 18 numeric columns on screen; these
   * two per group take it to 30. Most readings of this report are the
   * estimated/actual/balance question it was rebuilt for, so the extra pair is
   * opt-in rather than a permanent tax on width.
   */
  const [showCommitted, setShowCommitted] = useState(false);

  return (
    <>
      <style>{css}</style>
      <StateView query={q} isEmpty={() => false} loading="Building the job cost report…">
        {(data) => (
          <Body
            data={data} compact={compact}
            showZeroRows={showZeroRows} onToggleZeroRows={setShowZeroRows}
            showCommitted={showCommitted} onToggleCommitted={setShowCommitted}
          />
        )}
      </StateView>
    </>
  );
}

// A row hides only when EVERY group cell is empty, not when the TOTALS are: a
// +$100 Labor estimate offset by a −$100 Material credit nets to a zero total
// while both cells carry real figures — hiding that row would make the visible
// rows stop reconciling to the section totals, which still include the amounts
// (review, #352). All-groups-zero implies totals-zero, so this is strictly
// stricter. Balance needs no check: it is estimated − actual per cell.
// Row-visibility rule lives in ./jobCostRows so it can be unit-tested — importing
// this file in a spec would pull in apiClient and run MSAL at module load.
import { isZeroRow } from './jobCostRows';

function Body({
  data, compact, showZeroRows, onToggleZeroRows, showCommitted, onToggleCommitted,
}: {
  data: Report;
  compact: boolean;
  showZeroRows: boolean;
  onToggleZeroRows: (v: boolean) => void;
  showCommitted: boolean;
  onToggleCommitted: (v: boolean) => void;
}) {
  const { header, groups, report, provenance } = data;

  // showCommitted is in the deps AND passed through: turning the columns on has
  // to re-run the filter, or a committed-only row stays hidden behind a memo.
  const visible = useMemo(() => ({
    costCodes: showZeroRows ? report.costCodes.rows
      : report.costCodes.rows.filter((r) => !isZeroRow(r, showCommitted)),
    changeOrders: showZeroRows ? report.changeOrders.rows
      : report.changeOrders.rows.filter((r) => !isZeroRow(r, showCommitted)),
  }), [report, showZeroRows, showCommitted]);

  const hiddenCount =
    (report.costCodes.rows.length - visible.costCodes.length) +
    (report.changeOrders.rows.length - visible.changeOrders.length);

  const hasAnyRow = report.costCodes.rows.length > 0 || report.changeOrders.rows.length > 0;

  // Sub-columns per group band. Drives BOTH the header colSpans and the section
  // label's colSpan, so they cannot disagree when the toggle flips.
  const subCols = showCommitted ? 5 : 3;

  return (
    <div className={compact ? 'jcr-compact' : undefined}>
      <div className="jcr-hd">
        <div>
          <h2>Job Cost Management Report</h2>
          <p>
            Estimated against actual cost for every cost code on this job, by cost group.
            Balance is Estimated − Actual, so a negative balance is over the estimate.
            {showCommitted ? ' Committed is open PO and subcontract value not yet invoiced; Exposure is what is left after both — it does not include work still to be bought.' : ''}
          </p>
        </div>
        <button className="jcr-btn" onClick={() => exportWorkbook(data)} disabled={!hasAnyRow}>
          Export to Excel
        </button>
      </div>

      <div className="jcr-facts">
        <Fact label="Job" value={`${header.jobNumber} — ${header.jobName}`} />
        <Fact label="Customer" value={header.customer} />
        <Fact label="Project Manager" value={header.projectManager} />
        <Fact label="Estimator" value={header.estimator} />
        {/* Legacy had a Billing Person line; PMT has no such field, so the label
            appears only if one is ever added rather than showing a permanent dash. */}
        {header.billingPerson ? <Fact label="Billing Person" value={header.billingPerson} /> : null}
        <Fact label="As of" value={fmtDateTime(provenance.generatedAt)} />
      </div>

      <Provenance provenance={provenance} showCommitted={showCommitted} />

      {!hasAnyRow ? (
        <div className="jcr-wrap"><p className="jcr-empty">No estimate or actual cost has synced for this job yet.</p></div>
      ) : (
        <>
          <div className="jcr-wrap">
            <table className="jcr-table">
              <thead>
                <tr>
                  <th className="first p1" rowSpan={2}>Cost Code</th>
                  <th className="first p2" rowSpan={2}>Description</th>
                  {groups.map((g, i) => <th key={g} className={isBandStart(i) ? 'grp jcr-bs' : 'grp'} colSpan={subCols}>{groupLabel(g)}</th>)}
                  <th className={isBandStart(groups.length) ? 'grp jcr-bs' : 'grp'} colSpan={subCols}>Total</th>
                </tr>
                <tr>
                  {/* Keyed Fragment, not a bare `<>`: a bare fragment in a list
                      takes no key and React warns on every render. */}
                  {[...groups, 'Total' as const].map((g, i) => (
                    <Fragment key={g}>
                      <th className={isBandStart(i) ? 'jcr-bs' : undefined}>Estimated</th>
                      <th>Actual</th>
                      <th>Balance</th>
                      {showCommitted ? (
                        <>
                          <th title="Open commitment: PO and subcontract value not yet invoiced">Committed</th>
                          {/* Named EXPOSURE, never "projected" or "forecast": it
                              counts nothing still to be bought, so it reads
                              optimistically mid-buyout. The forecast is the
                              Financials tab's. */}
                          <th title="Estimated − Actual − Committed. Exposure, not a forecast: work not yet bought is not included">Exposure</th>
                        </>
                      ) : null}
                    </Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                <SectionRows label="Cost Codes" rows={visible.costCodes} totals={report.costCodes.totals} groups={groups} showCommitted={showCommitted} />
                {report.changeOrders.rows.length > 0 ? (
                  <SectionRows label="Change Orders" rows={visible.changeOrders} totals={report.changeOrders.totals} groups={groups} showCommitted={showCommitted} />
                ) : null}
                <Row row={report.grandTotal} groups={groups} className="jcr-tot grand" showCommitted={showCommitted} />
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--ew-s-4)', marginTop: 'var(--ew-s-3)' }}>
            <label className="jcr-toggle">
              <input type="checkbox" checked={showZeroRows} onChange={(e) => onToggleZeroRows(e.target.checked)} />
              Show cost codes with no estimate and no actual
              {!showZeroRows && hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}
            </label>
            <label className="jcr-toggle">
              <input type="checkbox" checked={showCommitted} onChange={(e) => onToggleCommitted(e.target.checked)} />
              Show open commitment and exposure
            </label>
          </div>
        </>
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="jcr-fact">
      <div className="l">{label}</div>
      <div className="v">{value?.trim() || '—'}</div>
    </div>
  );
}

/** "Subcontract" is the internal group name; the report says "Subs", which is
 *  what the legacy sheet said and what people call it. */
const groupLabel = (g: JobCostGroup | 'Total') => (g === 'Subcontract' ? 'Subs' : g);

function SectionRows({
  label, rows, totals, groups, showCommitted,
}: {
  label: string;
  rows: JobCostRow[];
  totals: JobCostRow;
  groups: JobCostGroup[];
  showCommitted: boolean;
}) {
  // Must track the header's colSpan, or the section label stops spanning the
  // table the moment the committed columns are shown.
  const span = 2 + (groups.length + 1) * (showCommitted ? 5 : 3);
  return (
    <>
      <tr className="jcr-sec"><td className="first" colSpan={span}>{label}</td></tr>
      {rows.map((r) => <Row key={r.costCode ?? '(none)'} row={r} groups={groups} showCommitted={showCommitted} />)}
      {/* The section total covers EVERY row in the section, including any hidden
          by the zero-row filter. That is only ever the all-zero rows, so the
          filter cannot move a total — but the total is deliberately taken from
          the server's figure rather than recomputed from the visible rows, so it
          can't drift if the filter ever grows teeth. */}
      <Row row={totals} groups={groups} className="jcr-tot" showCommitted={showCommitted} />
    </>
  );
}

function Row({ row, groups, className, showCommitted }: { row: JobCostRow; groups: JobCostGroup[]; className?: string; showCommitted: boolean }) {
  return (
    <tr className={className}>
      {/* Ellipsised like the description beside it, with the full code on hover:
          the column is a fixed width, and a code longer than it expects must not
          slide silently under the next cell. */}
      <td className="first p1" title={row.costCode ?? undefined}>{row.costCode ?? (className ? '' : '(no cost code)')}</td>
      {/* Fixed-width and ellipsised, so the full name is available on hover
          rather than lost — some cost-code names run well past 240px. */}
      <td className="first p2" title={row.description ?? undefined}>{row.description ?? ''}</td>
      {groups.map((g, i) => <Cells key={g} cell={row.byGroup[g]} showCommitted={showCommitted} bandStart={isBandStart(i)} />)}
      <Cells cell={row.total} showCommitted={showCommitted} bandStart={isBandStart(groups.length)} />
    </tr>
  );
}

function Cells({
  cell, showCommitted, bandStart,
}: {
  cell: { estimated: number; actual: number; balance: number; committed: number; exposure: number };
  showCommitted: boolean;
  /** First column of a band that has another band to its left — draws the band line. */
  bandStart: boolean;
}) {
  return (
    <>
      <td className={bandStart ? 'num jcr-bs' : 'num'}>{money(cell.estimated)}</td>
      <td className="num">{money(cell.actual)}</td>
      <td className={`num ${balanceClass(cell.balance)}`}>{money(cell.balance)}</td>
      {showCommitted ? (
        <>
          {/* Committed is NOT colored over/under — it is neither. It is money
              promised, and coloring it would read as a verdict on a number that
              is only an obligation. */}
          <td className="num jcr-com">{money(cell.committed)}</td>
          <td className={`num ${balanceClass(cell.exposure)}`}>{money(cell.exposure)}</td>
        </>
      ) : null}
    </>
  );
}

/**
 * What the reader needs to know before trusting the figures.
 *
 * A $0 column means something completely different depending on whether its
 * Intacct feed is switched on, and a management report that doesn't say which is
 * how people end up managing to a number that isn't there.
 */
function Provenance({ provenance: p, showCommitted }: { provenance: Report['provenance']; showCommitted: boolean }) {
  const notes: { kind: 'warn' | 'info'; text: string }[] = [];

  /**
   * Only while the columns are on screen — a warning about a figure nobody is
   * looking at is noise.
   *
   * ⚠️ This is the difference between OPEN and TOTAL commitment, and now also
   * between a commitment and a draft. A line can be missing its invoiced-to-date
   * (so it contributes its full order value), its document state (so a Draft or
   * Closed PO is still counted), its quantity remaining (so price variance on an
   * already-received line is still counted), or its revised value (so posted
   * change orders are missing altogether).
   *
   * ⚠️ THE FIRST THREE OVERSTATE; THE FOURTH UNDERSTATES (#405). This note said
   * "OVERSTATES" until the revised value joined the count, and that claim is no
   * longer safe to make: a job whose POs carry approved increases reads LOW while
   * unsynced — one job in the portfolio reads $0 against a true $207,687. So the
   * wording says the figure is not reliable yet and names both directions, rather
   * than telling a PM the number is high when it may be low.
   */
  const plural = (n: number) => (n === 1 ? '' : 's');

  if (showCommitted && p.committedLinesNotFullySynced > 0) {
    notes.push({
      kind: 'warn',
      // ⚠️ "IF THEY STILL EXIST IN INTACCT" is load-bearing (review, #376). A line
      // deleted upstream can never resolve, because the sync only ever MERGEs
      // what Intacct returns — so a flat "a sync clears this" would be false for
      // those and would invite someone running syncs that cannot help. This
      // wording is true either way, which is why no code has to tell them apart.
      text: `${p.committedLinesNotFullySynced} PO or subcontract line${plural(p.committedLinesNotFullySynced)} `
        + 'on this job have not finished syncing, so Committed is not reliable yet and can be wrong in either '
        + 'direction. It may read HIGH — showing their full order value rather than what is still open, or '
        + 'counting draft orders or price variance on orders already received in full. It may also read LOW, '
        + 'because approved change orders that raised a PO are not counted until the line syncs. '
        + 'A Sage sync (Admin › Integrations) picks these up if '
        + 'they still exist in Intacct; any that persist are orders Intacct no longer returns.',
    });
  }

  // Committed joins this list only while its columns are on — warning about a
  // feed behind a column nobody is looking at is noise. All three flags gate the
  // same thing (the nightly pull), so they get the same treatment.
  const off = [
    !p.estimateEnabled && 'estimate',
    !p.actualEnabled && 'actual',
    showCommitted && !p.committedEnabled && 'committed',
  ].filter(Boolean) as string[];
  if (off.length) {
    const list = off.length > 1 ? `${off.slice(0, -1).join(', ')} and ${off[off.length - 1]}` : off[0];
    notes.push({ kind: 'warn', text: `The Intacct ${list} sync is switched off, so those columns are not live.` });
  }
  if (!p.estimateSynced) {
    notes.push({
      kind: 'warn',
      text: 'No estimate lines have synced for this job, so every Estimated figure is $0 — that is missing data, not a zero budget.',
    });
  }
  if (p.pendingChange.rows > 0) {
    notes.push({
      kind: 'info',
      text: `Estimated includes ${money(p.pendingChange.amount)} of PENDING change scope `
        + `(${p.pendingChange.rows} estimate line${p.pendingChange.rows === 1 ? '' : 's'}). `
        + 'Left in so this report ties to the Financials tab.',
    });
  }

  return (
    <>
      {notes.map((n, i) => <div key={i} className={`jcr-note ${n.kind}`}>{n.text}</div>)}
      <div className="jcr-note info">
        Estimated from the Intacct job estimate (synced {fmtDateTime(p.estimateSyncedAt)}); actual from Intacct GL
        job cost (synced {fmtDateTime(p.actualSyncedAt)}).
        {/* Each feed states its OWN sync time. The "As of" fact at the top is
            when the report was BUILT, which says nothing about how old any
            individual feed is — a paused PO sync would otherwise show stale
            commitment under a fresh timestamp (review, #358). */}
        {showCommitted ? (
          <> Committed from Intacct purchase orders and subcontracts
            (synced {fmtDateTime(p.committedSyncedAt)}).</>
        ) : null}
        {' '}Cost codes are grouped per line, so a code can carry both
        labor and material — the Financials tab files each code under a single group instead, which is why a column
        here can differ from that tab even though the job totals agree.
      </div>
    </>
  );
}

/**
 * Export the report as a workbook, one sheet, laid out like the sheet it
 * replaces: header block, the two code sections, the three totals rows.
 *
 * Numbers go out as NUMBERS, not preformatted strings, because the first thing a
 * PM does with this file is add a column of their own.
 */
function exportWorkbook(data: Report) {
  const { header, groups, report, provenance } = data;
  const glabels = [...groups.map(groupLabel), 'Total'];

  /**
   * ⚠️ Committed and Exposure are ALWAYS exported, regardless of the on-screen
   * toggle — matching how this export already ignores the zero-row filter and
   * writes every row. A spreadsheet is where someone wants the complete data,
   * and hiding a column there is their own business. Keeping it unconditional
   * also means the file's column count never depends on UI state, which is what
   * makes a saved template keep working.
   */
  const cells = (c: JobCostRow['total']) => [c.estimated, c.actual, c.balance, c.committed, c.exposure];

  const rowCells = (r: JobCostRow) => [
    r.costCode ?? '(no cost code)',
    r.description ?? '',
    ...groups.flatMap((g) => cells(r.byGroup[g])),
    ...cells(r.total),
  ];

  const aoa: (string | number | null)[][] = [
    [`Job Cost Management Report — as of ${fmtDateTime(provenance.generatedAt)}`],
    [],
    ['Job', `${header.jobNumber} — ${header.jobName}`],
    ['Customer', header.customer ?? ''],
    ['Project Manager', header.projectManager ?? ''],
    ['Estimator', header.estimator ?? ''],
    ...(header.billingPerson ? [['Billing Person', header.billingPerson]] : []),
    [],
    // Group band, then the five-column band beneath it. Written as two rows
    // rather than merged cells so the file stays sortable/filterable.
    ['', '', ...glabels.flatMap((g) => [g, '', '', '', ''])],
    ['Cost Code', 'Description',
      ...glabels.flatMap(() => ['Estimated', 'Actual', 'Balance', 'Committed (open)', 'Exposure'])],
  ];

  aoa.push(['COST CODES']);
  for (const r of report.costCodes.rows) aoa.push(rowCells(r));
  aoa.push(rowCells(report.costCodes.totals));

  if (report.changeOrders.rows.length) {
    aoa.push([]);
    aoa.push(['CHANGE ORDERS']);
    for (const r of report.changeOrders.rows) aoa.push(rowCells(r));
    aoa.push(rowCells(report.changeOrders.totals));
  }

  aoa.push([]);
  aoa.push(rowCells(report.grandTotal));

  // The provenance travels WITH the file. A workbook that leaves the app without
  // saying which feeds were live is the same trap as an on-screen $0 with no note.
  aoa.push([]);
  aoa.push(['Committed sync enabled', provenance.committedEnabled ? 'yes' : 'NO — the nightly PO/subcontract pull is switched off']);
  aoa.push(['Estimated source', `Intacct job estimate, synced ${fmtDateTime(provenance.estimateSyncedAt)}`]);
  aoa.push(['Actual source', `Intacct GL job cost, synced ${fmtDateTime(provenance.actualSyncedAt)}`]);
  aoa.push(['Committed source', `Intacct purchase orders + subcontracts, synced ${fmtDateTime(provenance.committedSyncedAt)}`]);
  aoa.push(['Committed basis', 'OPEN commitment — order value not yet invoiced, clamped per line so an over-invoiced line cannot offset another cost code.']);
  aoa.push(['Exposure basis', 'Estimated - Actual - Committed. NOT a forecast: work not yet bought is excluded. The projected final cost is on the Financials tab.']);
  if (provenance.committedLinesNotFullySynced > 0) {
    aoa.push(['⚠', `${provenance.committedLinesNotFullySynced} PO/subcontract line(s) have not finished syncing, so Committed is not reliable yet and can be wrong in either direction: it may read high (their full order value rather than what is still open, or draft orders and price variance on orders already received in full), or low (approved change orders that raised a PO are not counted until the line syncs).`]);
  }
  if (!provenance.estimateSynced) aoa.push(['⚠', 'No estimate lines have synced for this job — Estimated is missing, not zero.']);
  if (!provenance.estimateEnabled) aoa.push(['⚠', 'The Intacct estimate sync is switched off.']);
  if (!provenance.actualEnabled) aoa.push(['⚠', 'The Intacct GL cost sync is switched off.']);
  if (provenance.pendingChange.rows > 0) {
    aoa.push(['Note', `Estimated includes ${money(provenance.pendingChange.amount)} of pending change scope.`]);
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 15 }, { wch: 38 }, ...Array.from({ length: glabels.length * 5 }, () => ({ wch: 14 }))];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Job Cost');
  // ⚠️ The EXPORTER'S calendar date, not the UTC one: an Eastern user exporting
  // after 8pm is already on the next UTC day and would file a workbook stamped
  // TOMORROW. Same reason GlDetailDrawer uses this helper.
  XLSX.writeFile(wb, `job-cost-management-${header.jobNumber || 'job'}-${todayInputValue()}.xlsx`);
}
